// Sky, sun, and a very large ocean for a very large ship. Fixed golden
// late-afternoon light — the Big J photographs well at 1700.

import * as THREE from "three";

export function makeSea(scene) {
  scene.background = new THREE.Color(0x9db8d6);
  scene.fog = new THREE.Fog(0xa8bdd4, 6000, 60000);

  const hemi = new THREE.HemisphereLight(0xc6d7ec, 0x54626e, 1.1);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffe3b0, 1.3);
  sun.position.set(-2600, 1500, 900);
  scene.add(sun);
  // cool fill from the far side so the shadow side still reads as haze gray
  const fill = new THREE.DirectionalLight(0xa8c0dc, 0.4);
  fill.position.set(2400, 900, -1400);
  scene.add(fill);

  // Sky dome: vertical gradient, warm toward the sun-side horizon. The
  // `night` uniform crossfades the whole dome to a moonless-dark blue with a
  // salted starfield (blocky stars — house style).
  const skyGeo = new THREE.SphereGeometry(48000, 24, 12);
  const skyMat = new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false,
    uniforms: { sunDir: { value: sun.position.clone().normalize() }, night: { value: 0 } },
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      varying vec3 vDir;
      uniform vec3 sunDir;
      uniform float night;
      void main() {
        float h = clamp(vDir.y, 0.0, 1.0);
        vec3 zenith = vec3(0.32, 0.48, 0.72);
        vec3 horizon = vec3(0.82, 0.78, 0.72);
        vec3 c = mix(horizon, zenith, pow(h, 0.55));
        float s = pow(max(dot(normalize(vDir), sunDir), 0.0), 6.0);
        c += vec3(1.0, 0.72, 0.38) * s * 0.55;
        float disc = smoothstep(0.9993, 0.9997, dot(normalize(vDir), sunDir));
        c += vec3(1.0, 0.9, 0.7) * disc * 2.0;
        // night: deep blue-black, faint milky band low on the sky, stars
        vec3 nz = vec3(0.012, 0.022, 0.05);
        vec3 nh = vec3(0.045, 0.06, 0.10);
        vec3 nc = mix(nh, nz, pow(h, 0.5));
        vec3 sd = normalize(vDir);
        float hash = fract(sin(dot(floor(sd * 340.0), vec3(12.9898, 78.233, 37.719))) * 43758.5453);
        float star = step(0.9988, hash) * smoothstep(0.02, 0.12, sd.y) * (0.4 + 0.6 * fract(hash * 91.7));
        nc += vec3(0.9, 0.93, 1.0) * star;
        gl_FragColor = vec4(mix(c, nc, night), 1.0);
      }`,
  });
  const sky = new THREE.Mesh(skyGeo, skyMat);
  sky.name = "sky";
  scene.add(sky);

  // Ocean: one huge camera-centered disc with gentle shader swell + moving
  // glint noise. NOT a CircleGeometry: the disc recenters on the camera every
  // frame, so a single center vertex would ride exactly on the camera's near
  // plane whenever the view is level — and clipping a 48 km triangle anchored
  // at w≈0 interpolates garbage depth across the whole below-horizon half of
  // the frame. The sea then paints OVER geometry that is meters in front of
  // the lens: compartment floors vanish under open water, the periscope
  // (pitch 0.02 — dead in the window) loses its target picture. Log-spaced
  // rings keep every triangle near the camera small, so depth stays honest
  // at any pitch. Found by a periscope playtest, of all things.
  const seaGeo = (() => {
    const SEG = 96;
    const radii = [0, 2];
    while (radii[radii.length - 1] < 48000) radii.push(Math.min(48000, radii[radii.length - 1] * 2.4));
    const pos = [0, 0, 0], nrm = [0, 1, 0], idx = [];
    for (let r = 1; r < radii.length; r++)
      for (let s = 0; s < SEG; s++) {
        const a = (s / SEG) * Math.PI * 2;
        pos.push(Math.cos(a) * radii[r], 0, -Math.sin(a) * radii[r]); // -sin: CCW from above
        nrm.push(0, 1, 0);
      }
    const at = (r, s) => (r === 0 ? 0 : 1 + (r - 1) * SEG + (s % SEG));
    for (let s = 0; s < SEG; s++) idx.push(0, at(1, s), at(1, s + 1)); // center cap
    for (let r = 1; r < radii.length - 1; r++)
      for (let s = 0; s < SEG; s++) {
        const a = at(r, s), b = at(r, s + 1), c = at(r + 1, s), d = at(r + 1, s + 1);
        idx.push(a, c, b, b, c, d);
      }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
    g.setIndex(idx);
    return g;
  })();
  const uni = { time: { value: 0 } };
  const mat = new THREE.MeshPhongMaterial({ color: 0x1f4d68, specular: 0x2a3947, shininess: 22 });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.time = uni.time;
    shader.vertexShader = `uniform float time;\nvarying vec2 vXZ;\n` + shader.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>
       vXZ = vec2(position.x, position.z);`
    );
    shader.fragmentShader = `uniform float time;\nvarying vec2 vXZ;\n` + shader.fragmentShader.replace(
      "#include <color_fragment>",
      `#include <color_fragment>
       {
         vec2 p = vXZ * 0.045;
         float w = sin(p.x * 1.7 + time * 0.9) * sin(p.y * 1.3 - time * 0.7)
                 + 0.5 * sin(p.x * 4.3 - time * 1.7) * sin(p.y * 3.1 + time * 1.1);
         float lane = 0.04 * w;
         diffuseColor.rgb += vec3(lane * 0.5, lane * 0.85, lane);
       }`
    );
  };
  const sea = new THREE.Mesh(seaGeo, mat);
  sea.name = "sea";
  sea.position.y = 0;
  scene.add(sea);

  return {
    sea, sky, sun, hemi,
    update(dt, cameraWorldPos) {
      uni.time.value += dt;
      // keep the horizon centered on the viewer
      sea.position.x = cameraWorldPos.x; sea.position.z = cameraWorldPos.z;
      sky.position.x = cameraWorldPos.x; sky.position.z = cameraWorldPos.z;
    },
    // Douse the sun for a night action: dark dome with stars, black water
    // with hard moon-glints (so star shells and burning ships read on it),
    // and the scene lights down to starlight.
    setNight() {
      scene.background = new THREE.Color(0x070d17);
      scene.fog = new THREE.Fog(0x070d17, 3500, 40000);
      skyMat.uniforms.night.value = 1;
      hemi.intensity = 0.4; hemi.color.set(0x2e3c56); hemi.groundColor.set(0x0e141c);
      sun.intensity = 0.16; sun.color.set(0xaebfe0); // a low moon, same azimuth
      fill.intensity = 0.06;
      mat.color.set(0x0a1622);
      mat.specular.set(0x5a7290);
      mat.shininess = 52;
    },
  };
}
